<!-- 
Guided Tour component

This component is used to display the guided tour.
It uses the intro.js library to display the guided tour.

Props:
- tourType: string
  - Type of guided tour to be displayed. Currently, only 'teacher-dashboard' is supported.
- tourOptions: object

-->

<script setup lang="ts">
/**
 * Imports
 */
import introJs from 'intro.js'; // intro.js library is used to display the guided tour

/**
 * PROPERTIES / PROPS0
 * (These are variables that are passed to the component from a parent component)
 */
const props = defineProps<{
  options: {
    disableScroll?: boolean;
    tooltipClass?: string;
    scrollTopForFloatingTooltip?: boolean;
    autoPosition?: boolean;
    helperElementPadding?: number;
  };
  steps: GuidedTourStep[];
}>();

/**
 * EMITS / OUTPUTS
 * (events emitted by the component, handled by parents)
 */
const emit = defineEmits<{
  // Emits the event when the guided tour is closed
  'guided-tour-exit': [];
}>();

/**
 * Interface for defining the structure of guided tour steps
 */
export interface GuidedTourStep {
  element?: string; // The element's selector string to highlight during the step
  descHeading: string; // The heading of the step description
  desc: string; // The description of the step
  image?: {
    src: string; // The source URL of the image for the step
    alt: string; // The alt text for the image
  };
  position?:
    | 'bottom-right-aligned'
    | 'bottom-left-aligned'
    | 'bottom-middle-aligned'; // The position of the tooltip for the step
}

/**
 * Start Guilded Tour function
 */
const startGuidedTour = () => {
  /**
   * Check if the environment is development or production
   * and set the image base path accordingly
   */
  const isDevEnv = useRuntimeConfig().public.appEnv == 'local';
  let closeImagePath;
  if (isDevEnv) {
    closeImagePath = '/_nuxt/assets/img/close-guided-tour.svg';
  } else {
    closeImagePath =
      useRuntimeConfig().public.cdnURL + '/_nuxt/close-guided-tour.svg';
  }

  //generate steps
  const guidedTourSteps = generateSteps(props.steps);
  //initialize guidedTour
  const guidedTour = introJs();

  if (props.options.disableScroll) document.body.style.overflowY = 'hidden';

  /**
   * Options for guidedTour
   */
  const guidedTourOptions = {
    showBullets: false,
    showStepNumbers: true,
    hidePrev: true,
    tooltipPosition: 'bottom-middle-aligned',
    tooltipClass: props.options.tooltipClass
      ? `walkthrough-tooltip ${props.options.tooltipClass}`
      : 'walkthrough-tooltip',
    highlightClass: 'walkthrough-helperLayer',
    exitOnOverlayClick: false,

    disableInteraction: true,
    overlayOpacity: 0.2,
    skipLabel: `<img src="${closeImagePath}" alt="Close Guided Tour"></img>`,
    scrollToElement: true,
    keyboardNavigation: true,
    autoPosition:
      props.options.autoPosition !== undefined
        ? props.options.autoPosition
        : true,
    steps: guidedTourSteps,
    helperElementPadding: props.options.helperElementPadding
      ? props.options.helperElementPadding
      : 10
  };

  // set waklkthrough options
  guidedTour.setOptions(guidedTourOptions);

  // scroll to top for floating tooltip
  if (props.options.scrollTopForFloatingTooltip) {
    guidedTour.onchange((targetElement: Element) => {
      if (targetElement.classList.contains('introjsFloatingElement')) {
        //Added a setTimeout,
        // as introjs might be overriding the scroll position(in case no timeout is used)
        setTimeout(() => {
          window.scrollTo(0, 0);
        }, 0);
      }
    });
  }

  const elementsToDisable = Array.from(
    document.querySelectorAll('button, a, input')
  );

  // on exit of guidedTour
  guidedTour.onexit(() => {
    removeTabIndex(elementsToDisable);
    if (props.options.disableScroll) document.body.style.overflowY = 'visible';

    // emit event to parent component
    emit('guided-tour-exit');
  });

  //add tabindex to all the elements.
  addTabIndex(elementsToDisable);
  guidedTour.start();
};

/**
 * Function to add tabindex to all the elements
 */
const addTabIndex = (elements: Element[]) => {
  elements.forEach((element) => {
    element.setAttribute('tabindex', '-1');
  });
};

/**
 * Function to remove tabindex from all the elements
 */
const removeTabIndex = (elements: Element[]) => {
  elements.forEach((element) => {
    element.removeAttribute('tabindex');
  });
};

/**
 * Function to generate steps for introjs
 * @param steps : steps array
 * @returns Array of objects that introjs supports
 * The method will generate divs based on steps array (image div/description container etc)
 */
const generateSteps = (steps: GuidedTourStep[]) => {
  return steps?.map((step) => {
    let imageContainer = '';
    if (step.image && step.image.src) {
      const imageSrc = step.image.src;
      const imageAlt = step.image.alt ? step.image.alt : '';
      imageContainer = `<div class="image-container"><img src="${imageSrc}" alt="${imageAlt}"></div>`;
    }
    const intro = `
            <div class="intro-top">
                <div class="description-container">
                    <p class="description-heading">${step.descHeading}</p>
                    <p class="description">${step.desc}</p>
                </div>
            ${imageContainer}
            </div>`;
    return { intro, position: step.position, element: step.element };
  });
};

/**
 * Start the guidedTour
 */
startGuidedTour();
</script>

<style lang="scss">
// Styles used for guided tour.
@import '../../../assets/scss/introjs.scss';
@import '../../../assets/scss/guidedtour.scss';
</style>
